/*
---------------------------------------------------------
Copyright © 2026 Daniel Woik (DW E-Commerce). All rights reserved.
Custom-coded by DW E-Commerce. This code is licensed for use on
this store only, as part of the DW E-Commerce optimization service.
Copying, reselling, redistributing or using it on any other store
is not permitted without written permission.
---------------------------------------------------------
DW Kit:   Menu
Version:  1.6.0
Updated:  2026-10-16 — The page overlay can also show under the small dropdowns (section setting).
Source:   DW-Component-Library/Kits/Menu

FUNCTION LOCKED. Theme-independent.
Mobile: any element with [data-dw-menu-open] opens the drawer; panels
slide per level; X, overlay, Esc and Back close or go up one level.
Desktop: header links with [data-dw-menu-item="<index>"] open the matching
mega menu or dropdown under the header (hover with a short delay, or click,
per section setting). The theme's own submenus must be removed.
Window API: window.DWMenu.open(), .close()
DW store override: LCP fixes 2026-10-27, backport from Menu 1.12.0: menu images (data-dw-menu-src /
data-dw-menu-srcset) are swapped in when their panel opens (desktop: on hover or
focus of the item). Marked below.
---------------------------------------------------------
*/
(function () {
  if (window.customElements.get('dw-menu')) return;

  var DESKTOP = '(min-width: 990px)';

  class DWMenu extends HTMLElement {
    connectedCallback() {
      this.drawer = this.querySelector('[data-dw-drawer]');
      this.layer = this.querySelector('[data-dw-mega-layer]');
      this.veil = this.querySelector('[data-dw-menu-veil]');
      this.openPanel = null;
      this.openTrigger = null;
      this.closeTimer = null;
      this.lastFocus = null;
      this.stack = ['root'];

      var self = this;
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () { self.classList.add('dw-is-ready'); });
      });

      this.onDocClick = this.onDocClick.bind(this);
      this.onKey = this.onKey.bind(this);
      this.onOver = this.onOver.bind(this);
      this.onOut = this.onOut.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onFocus = this.onFocus.bind(this); /* DW store override: LCP fixes 2026-10-27, backport from Menu 1.12.0 */
      document.addEventListener('click', this.onDocClick, true);
      document.addEventListener('keydown', this.onKey);
      document.addEventListener('pointerover', this.onOver);
      document.addEventListener('pointerout', this.onOut);
      /* DW store override: LCP fixes 2026-10-27, backport from Menu 1.12.0 */
      document.addEventListener('focusin', this.onFocus);
      this.addEventListener('pointerdown', function (event) {
        var next = event.target instanceof Element ? event.target.closest('[data-dw-panel-open]') : null;
        if (next) self.loadImages(self.querySelector('[data-dw-panel="' + next.getAttribute('data-dw-panel-open') + '"]'));
      });
      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('scroll', this.onResize, { passive: true });
      this.linkLocale();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick, true);
      document.removeEventListener('keydown', this.onKey);
      document.removeEventListener('pointerover', this.onOver);
      document.removeEventListener('pointerout', this.onOut);
      document.removeEventListener('focusin', this.onFocus); /* DW store override: LCP fixes 2026-10-27, backport from Menu 1.12.0 */
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('scroll', this.onResize);
    }

    /* The country row opens the DW Locale Popup, wherever the theme rendered it. */
    linkLocale() {
      var row = this.querySelector('[data-dw-menu-locale]');
      if (!row) return;
      var popup = document.querySelector('[id^="dw-locale-"][role="dialog"], .dw-locale[id^="dw-locale-"]');
      if (popup && popup.id) row.setAttribute('aria-controls', popup.id);
      else row.hidden = true;
    }

    /* DW store override: LCP fixes 2026-10-27, backport from Menu 1.12.0: menu images are never requested before their panel opens; once loaded they stay. */
    loadImages(scope) {
      if (!scope) return;
      scope.querySelectorAll('img[data-dw-menu-src]').forEach(function (img) {
        if (img.dataset.dwMenuSrcset) { img.srcset = img.dataset.dwMenuSrcset; img.removeAttribute('data-dw-menu-srcset'); }
        img.src = img.dataset.dwMenuSrc;
        img.removeAttribute('data-dw-menu-src');
      });
    }

    megaPanel(trigger) {
      return this.layer ? this.layer.querySelector('[data-dw-mega-panel="' + trigger.getAttribute('data-dw-menu-item') + '"]') : null;
    }

    onFocus(event) {
      var t = event.target instanceof Element ? event.target.closest('[data-dw-menu-item]') : null;
      if (t && window.matchMedia(DESKTOP).matches) this.loadImages(this.megaPanel(t));
    }

    /* ---------- Mobile drawer ---------- */
    open() {
      if (this.classList.contains('dw-is-open')) return;
      this.closeMega();
      this.lastFocus = document.activeElement;
      this.showPanel('root', true);
      this.classList.add('dw-is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('dw-menu-open');
      document.documentElement.style.overflow = 'hidden';
      document.querySelectorAll('[data-dw-menu-open]').forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
      var self = this;
      var drawer = this.drawer;
      var focusIn = function () {
        if (self.classList.contains('dw-is-open') && !drawer.contains(document.activeElement)) drawer.focus({ preventScroll: true });
      };
      window.setTimeout(focusIn, 50);
      window.setTimeout(focusIn, 400);
    }

    close() {
      if (!this.classList.contains('dw-is-open')) return;
      this.classList.remove('dw-is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('dw-menu-open');
      document.documentElement.style.overflow = '';
      document.querySelectorAll('[data-dw-menu-open]').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
      var self = this;
      window.setTimeout(function () { if (!self.classList.contains('dw-is-open')) self.showPanel('root', true); }, 350);
    }

    showPanel(id, reset) {
      var panels = this.querySelectorAll('[data-dw-panel]');
      if (reset) this.stack = ['root'];
      var stack = this.stack || ['root'];
      panels.forEach(function (p) {
        var pid = p.getAttribute('data-dw-panel');
        var on = pid === id;
        p.classList.toggle('is-active', on);
        /* Levels in the open path move left, so a deeper level always slides in from the right. */
        p.classList.toggle('is-behind', !on && stack.indexOf(pid) > -1);
        p.setAttribute('aria-hidden', on ? 'false' : 'true');
      });
      this.loadImages(this.querySelector('[data-dw-panel="' + id + '"]')); /* DW store override: LCP fixes 2026-10-27, backport from Menu 1.12.0 */
      var active = this.querySelector('[data-dw-panel="' + id + '"] .dw-menu__scroll');
      if (active && reset) active.scrollTop = 0;
    }

    /* ---------- Desktop menus ---------- */
    headerBottom(trigger) {
      var header = trigger.closest('header, [id*="header"], .header, .shopify-section-group-header-group') || trigger.parentElement;
      var bottom = header.getBoundingClientRect().bottom;
      var row = trigger.closest('nav, ul, .menu-list, [role="menubar"]');
      if (row) bottom = Math.max(bottom, row.getBoundingClientRect().bottom);
      return Math.max(0, Math.round(bottom));
    }

    openMega(index, trigger) {
      if (!window.matchMedia(DESKTOP).matches) return false;
      var panel = this.layer.querySelector('[data-dw-mega-panel="' + index + '"]');
      if (!panel) return false;
      window.clearTimeout(this.closeTimer);
      if (this.openPanel === panel) return true;
      this.closeMega(true);
      this.loadImages(panel); /* DW store override: LCP fixes 2026-10-27, backport from Menu 1.12.0 */
      var top = this.headerBottom(trigger);
      this.style.setProperty('--dw-mega-top', top + 'px');
      if (panel.getAttribute('data-kind') === 'dropdown') {
        var r = trigger.getBoundingClientRect();
        var left = Math.max(8, Math.min(r.left, window.innerWidth - 328));
        panel.style.setProperty('--dw-drop-left', Math.round(left) + 'px');
      }
      panel.hidden = false;
      if (panel.getAttribute('data-kind') === 'mega' || this.getAttribute('data-dropdown-overlay') === 'true') this.veil.hidden = false;
      this.openPanel = panel;
      this.openTrigger = trigger;
      trigger.setAttribute('aria-expanded', 'true');
      trigger.classList.add('dw-menu-item--open');
      return true;
    }

    closeMega(now) {
      window.clearTimeout(this.closeTimer);
      if (!this.openPanel) return;
      this.openPanel.hidden = true;
      this.veil.hidden = true;
      if (this.openTrigger) {
        this.openTrigger.setAttribute('aria-expanded', 'false');
        this.openTrigger.classList.remove('dw-menu-item--open');
      }
      this.openPanel = null;
      this.openTrigger = null;
    }

    scheduleClose() {
      var self = this;
      window.clearTimeout(this.closeTimer);
      this.closeTimer = window.setTimeout(function () { self.closeMega(); }, 180);
    }

    onOver(event) {
      if (!window.matchMedia(DESKTOP).matches) return;
      var t = event.target instanceof Element ? event.target : null;
      if (!t) return;
      var trigger = t.closest('[data-dw-menu-item]');
      /* DW store override: LCP fixes 2026-10-27, backport from Menu 1.12.0: hover preloads the item's images in both open modes. */
      if (trigger) this.loadImages(this.megaPanel(trigger));
      if (this.dataset.openOn !== 'hover') return;
      if (trigger) {
        if (!this.openMega(trigger.getAttribute('data-dw-menu-item'), trigger)) this.scheduleClose();
        return;
      }
      if (this.openPanel && this.openPanel.contains(t)) window.clearTimeout(this.closeTimer);
    }

    onOut(event) {
      if (this.dataset.openOn !== 'hover' || !this.openPanel) return;
      var to = event.relatedTarget instanceof Element ? event.relatedTarget : null;
      if (to && (this.openPanel.contains(to) || (this.openTrigger && this.openTrigger.contains(to)))) return;
      var from = event.target instanceof Element ? event.target : null;
      if (!from) return;
      if (this.openPanel.contains(from) || (this.openTrigger && this.openTrigger.contains(from))) this.scheduleClose();
    }

    onResize() {
      if (this.openPanel && this.openTrigger) {
        if (!window.matchMedia(DESKTOP).matches) { this.closeMega(); return; }
        this.style.setProperty('--dw-mega-top', this.headerBottom(this.openTrigger) + 'px');
      }
    }

    onDocClick(event) {
      var t = event.target instanceof Element ? event.target : null;
      if (!t) return;

      var opener = t.closest('[data-dw-menu-open]');
      if (opener) {
        event.preventDefault();
        event.stopPropagation();
        if (this.classList.contains('dw-is-open')) this.close(); else this.open();
        return;
      }

      if (this.contains(t)) {
        if (t.closest('[data-dw-menu-close]')) { event.preventDefault(); this.close(); return; }
        /* Sign in: open the theme's Shopify account popup (it opens above the menu); otherwise follow the link. */
        if (t.closest('[data-dw-menu-account]')) {
          var account = document.querySelector('shopify-account');
          var accountButton = account && account.shadowRoot && account.shadowRoot.querySelector('button');
          if (accountButton) { event.preventDefault(); accountButton.click(); }
          return;
        }
        var next = t.closest('[data-dw-panel-open]');
        if (next) {
          event.preventDefault();
          var id = next.getAttribute('data-dw-panel-open');
          this.stack.push(id);
          this.showPanel(id);
          return;
        }
        if (t.closest('[data-dw-panel-back]')) {
          event.preventDefault();
          this.stack.pop();
          this.showPanel(this.stack[this.stack.length - 1] || 'root');
          return;
        }
        if (t.closest('[data-dw-menu-veil]')) { this.closeMega(); return; }
        return;
      }

      var trigger = t.closest('[data-dw-menu-item]');
      if (trigger && window.matchMedia(DESKTOP).matches) {
        var index = trigger.getAttribute('data-dw-menu-item');
        var panel = this.layer.querySelector('[data-dw-mega-panel="' + index + '"]');
        if (!panel) return;
        /* Click mode: first click opens, second follows the link. Hover mode: links work normally. */
        if (this.dataset.openOn === 'click') {
          if (this.openPanel === panel) return;
          event.preventDefault();
          this.openMega(index, trigger);
        }
        return;
      }

      if (this.openPanel) this.closeMega();
    }

    onKey(event) {
      if (event.key !== 'Escape') return;
      if (this.classList.contains('dw-is-open')) {
        if (document.querySelector('[id^="dw-locale-"][role="dialog"]:not([hidden])')) return;
        if (this.stack.length > 1) {
          this.stack.pop();
          this.showPanel(this.stack[this.stack.length - 1]);
        } else {
          this.close();
        }
        return;
      }
      if (this.openPanel) {
        var trigger = this.openTrigger;
        this.closeMega();
        if (trigger) trigger.focus();
      }
    }
  }

  customElements.define('dw-menu', DWMenu);

  window.DWMenu = {
    open: function () { var m = document.querySelector('dw-menu'); if (m) m.open(); },
    close: function () { var m = document.querySelector('dw-menu'); if (m) m.close(); }
  };

  /* Theme editor: open the drawer while the section or one of its blocks is selected. */
  document.addEventListener('shopify:section:select', function (e) {
    var m = document.querySelector('dw-menu');
    if (m && e.detail && m.getAttribute('data-section-id') === e.detail.sectionId && !window.matchMedia(DESKTOP).matches) m.open();
  });
  document.addEventListener('shopify:section:deselect', function (e) {
    var m = document.querySelector('dw-menu');
    if (m && e.detail && m.getAttribute('data-section-id') === e.detail.sectionId) m.close();
  });
})();
